/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, IconTile } from 'components/v3';

// The "?" info bubble beside a card title. The popup opens just below the icon
// but is positioned with JS so it can be clamped to the viewport — a plain CSS
// absolute box would run off-screen whenever the icon sits near an edge. The
// arrow tracks the icon via the --arrow-left custom property.
const MARGIN = 8; // min gap from the viewport edges
const MAX_WIDTH = 280;

const InfoTooltip = ({ icon, children }) => {
  const wrapRef = useRef(null);
  const [style, setStyle] = useState(null); // null = hidden

  const place = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(MAX_WIDTH, vw - MARGIN * 2);
    const iconCenter = r.left + r.width / 2;
    // Center under the icon, then clamp so the whole box stays on screen.
    let left = iconCenter - width / 2;
    left = Math.max(MARGIN, Math.min(left, vw - width - MARGIN));
    // Keep the arrow pointing at the icon within the (possibly shifted) box.
    const arrowLeft = Math.max(12, Math.min(iconCenter - left, width - 12));
    setStyle({
      position: 'fixed',
      top: r.bottom + 9,
      left,
      width,
      display: 'flex',
      '--arrow-left': `${arrowLeft}px`,
    });
  }, []);

  const hide = useCallback(() => setStyle(null), []);

  // While open, close on scroll/resize so the fixed popup can't drift away from
  // its icon.
  useEffect(() => {
    if (!style) return undefined;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [style, hide]);

  return (
    <span
      ref={wrapRef}
      className="card-title-tip"
      onMouseEnter={place}
      onMouseLeave={hide}
      onFocus={place}
      onBlur={hide}
      onClick={(e) => {
        e.stopPropagation();
        if (style) hide();
        else place();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          if (style) hide();
          else place();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <Icon name="circle-help" size={17} strokeWidth={2.2} />
      {style ? (
        <span className="tip-text" style={style}>
          <IconTile icon={icon} size={26} iconSize={15} />
          {children}
        </span>
      ) : null}
    </span>
  );
};

export default InfoTooltip;
